import * as bodyPix from '@tensorflow-models/body-pix';
import * as tf from '@tensorflow/tfjs';
import logger from '@/modules/Logger';
import UAParser from 'ua-parser-js';

/**
 * Implement Virtual Background using Tensorflow and BodyPix
 *
 * See documentation below for details
 * https://github.com/tensorflow/tfjs-models/tree/master/body-pix
 */
class VirtualBackground {
  constructor() {
    this.canvasTag = null;
    this.videoElement = null;
    this.bodyPixNet = null;
    this.segmentation = null;
    this.blurOption = {
      backgroundBlurAmount: 20,
      edgeBlurAmount: 6,
      flipHorizontal: false
    };
    this.effect = null;
    this.videoDevice = null;
    this.isMobile = new UAParser().getDevice().type === 'mobile';
    this.dispose = false;
    this.provider = null;
  }

  // load bodypix model
  async loadModel() {
    const options = {
      architecture: 'MobileNetV1',
      multiplier: 0.5,
      outputStride: 16,
      quantBytes: 2
    };
    const net = await bodyPix.load(options).catch(err => {
      logger.error('Error on loading bodyPix model', { operation: 'vbackground:load-model', error: err.message,event_type:78 });
    });
    this.bodyPixNet = net;
    logger.info('Background model is ready', { operation: 'vbackground:load-model', tfBackend: tf.getBackend() });
    return tf.getBackend() !== 'cpu';
  }

  // perform image processing on video element
  async performSegmentation(videoDevice) {
    if (this.effect === 'none') {
      return false;
    }
    this.dispose = false;
    this.videoDevice = videoDevice;

    if (this.provider === 'agora') {
      const videoStream = await this.getOriginalVideoStream();
      if (!videoStream) {
        return false;
      }
    }
    this.getElements();
    this.setBlurEffectAnimation();
    this.videoElement.play();
    return true;
  }

  setEffect(effect) {
    this.effect = effect;
    return this;
  }

  setProvider(provider) {
    this.provider = provider;
    return this;
  }

  // get original video to be modified before sending to agora rtc stream
  async getOriginalVideoStream() {
    return new Promise(resolve => {
      let video = document.querySelector('.for-process-video');

      const mobileMedia = {
        height: { ideal: 640, max: 640 },
        facingMode: { exact: 'user' }
      };
      const pcMedia = {
        width: 640,
        height: 360
      };
      const videoDevice = this.videoDevice ? { exact: this.videoDevice } : null;

      navigator.mediaDevices
        .getUserMedia({
          video: {
            deviceId: videoDevice,
            videoSource: null,
            ...(this.isMobile ? mobileMedia : pcMedia)
          },
          audio: false
        })
        .then(mediaStream => {
          if (video.srcObject) {
            video.srcObject.getTracks().forEach((track) => track.stop());
            video.srcObject = null;
          }
          video.srcObject = mediaStream;
        })
        .then(() => {
          // resolve the stream once the video state is loaded
          video.onloadeddata = () => {
            resolve(true);
          };
        })
        .catch(err => {
          logger.error('Error on getting video stream for blur effect', { operation: 'vbackground:user-media', err,event_type:79 });
          resolve(false);
        });
    });
  }

  // get needed elements for video processing
  getElements() {
    this.canvasTag = document.querySelector('.virtual-bg-video');
    this.videoElement = document.querySelector('.for-process-video');

    // scale down image size for better performance
    const multiplier = this.isMobile ? 0.60 : 0.95;
    this.videoElement.height = this.videoElement.videoHeight * multiplier;
    this.videoElement.width = this.videoElement.videoWidth * multiplier;
    this.canvasTag.height = this.videoElement.height;
    this.canvasTag.width = this.videoElement.width;
    this.canvasTag.setAttribute('data-blur', true);
  }

  // use request animation frame on mobile or firefox for less resource intensive animation
  async setBlurEffectAnimation() {
    if (this.isMobile || new UAParser().getBrowser().name === 'Firefox') {
      const loopFrame = async () => {
        if (this.effect !== 'none' && this.videoElement.readyState === 4 && !this.dispose) {
          await this.segmentPerson('low');
          this.applyBlurEffect();
        } else if (loopFrame) {
          cancelAnimationFrame(loopFrame);
        }
        requestAnimationFrame(loopFrame);
      };
      loopFrame();
    } else {
      while (this.effect !== 'none' && this.videoElement.readyState === 4 && !this.dispose) {
        await this.segmentPerson('medium');
        this.applyBlurEffect();
      }
    }
  }

  // predicts a corresponding segmentation of a person in the image
  async segmentPerson(resolution) {
    this.segmentation = await this.bodyPixNet.segmentPerson(this.videoElement, {
      internalResolution: resolution,
      segmentationThreshold: 0.65
    });
  }

  // apply blur effect on the video
  // video - original/plain video to be processed
  // canvas - canvas to draw the background-blurred image.
  applyBlurEffect() {
    bodyPix.drawBokehEffect(
      this.canvasTag,
      this.videoElement,
      this.segmentation,
      this.blurOption.backgroundBlurAmount,
      this.blurOption.edgeBlurAmount,
      this.blurOption.flipHorizontal
    );
    
    // add canvas bg to avoid the black on the transparent edge of video
    if (this.provider === 'agora') {
      const canvas = document.querySelector('.virtual-bg-video');
      let ctx = canvas.getContext('2d');
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = document.body.classList.contains('dark') ? '#141821': '#d3d6d8';
      ctx.fillRect(0, 0, this.canvasTag.width, this.canvasTag.height);
    }
  }

  disposeInitialCanvas() {
    this.dispose = true;
  }

  // disposes tensor from memory - tf.memory()
  releaseMemory() {
    this.dispose = true;
    this.bodyPixNet.dispose();
    tf.disposeVariables();
  }
}

export default VirtualBackground;
